export const DOMAIN = "https://www.directiveoptivesolutions.com";

export const BUSINESS = {
  name: "Directive Optive Solutions, LLC",
  shortName: "Directive Optive Solutions",
  tagline: "More than numbers. It’s personal.",
  phone: "(801) 474-5890",
  phoneHref: "tel:+18014745890",
  email: "info@directiveoptivesolutions.com",
  emailHref: "mailto:info@directiveoptivesolutions.com",
};

export const BOOKING_URL = "https://book.directiveoptivesolutions.com/book-online";

// Legacy Wix booking host (kept for reference/restore). The site now runs its own
// booking calendar at /book-online and /booking-calendar/*.

export const BOOKING_SERVICES = [
  {
    name: "Financial Statements / Financial Analysis",
    url: "https://book.directiveoptivesolutions.com/booking-calendar/financial-statements-financial-analysis",
    blurb: "A closer look at what your numbers are saying, with statements you can actually read.",
  },
  {
    name: "Account Setup",
    url: "https://book.directiveoptivesolutions.com/booking-calendar/account-setup",
    blurb: "Start from a clean, organized foundation instead of a shoebox of guesses.",
  },
  {
    name: "Bookkeeping Service",
    url: "https://book.directiveoptivesolutions.com/booking-calendar/bookkeeping-service",
    blurb: "Ongoing support that keeps your records current, reconciled, and ready.",
  },
];

export const NAV = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services" },
  { label: "Tax Planning", to: "/tax-planning" },
  { label: "About", to: "/about" },
  { label: "Resources", to: "/resources" },
  { label: "FAQ", to: "/faq" },
  { label: "Contact", to: "/contact" },
];

export const SERVICE_TOPICS = [
  {
    title: "Bookkeeping",
    for: "Business owners and the self-employed who want records that stay current.",
    solves: "Falling behind on transactions, unclear cash flow, year-end surprises.",
    includes: "Categorizing transactions, staying on top of payables and receivables, and keeping accounts tidy month to month.",
  },
  {
    title: "Bank Reconciliations",
    for: "Anyone whose books and bank statements don’t quite match.",
    solves: "Missing transactions, duplicates, and numbers you can’t vouch for.",
    includes: "Matching accounts to statements, flagging discrepancies, and a clean, explained ending balance.",
  },
  {
    title: "Accounts Payable & Receivable",
    for: "Teams juggling invoices, bills, and due dates by memory.",
    solves: "Late payments in, overdue bills out, and avoidable fees.",
    includes: "Tracking who owes what, scheduling payments, and simple follow-up routines.",
  },
  {
    title: "Payroll Support",
    for: "Small businesses with a handful of people on payroll.",
    solves: "Confusion about pay cycles, filings paperwork, and records falling out of sync.",
    includes: "Organizing payroll records and coordinating with your payroll provider. Exact scope is confirmed together.",
  },
  {
    title: "Budgeting",
    for: "Households and businesses that want a plan, not a guess.",
    solves: "Feast-or-famine months and spending that’s hard to see.",
    includes: "Building a realistic budget from your actual history and checking in on it.",
  },
  {
    title: "1099 Support",
    for: "Contractors and businesses that work with them.",
    solves: "January panic over missing forms and unclear requirements.",
    includes: "Helping you organize contractor information and prepare for 1099 season.",
  },
  {
    title: "Account Setup",
    for: "New businesses, new side hustles, and anyone starting fresh.",
    solves: "Messy beginnings that get harder to fix every month.",
    includes: "Setting up your chart of accounts and record-keeping structure from day one.",
  },
  {
    title: "Financial Statements & Reporting",
    for: "Owners who want to understand, not just archive, their numbers.",
    solves: "Reports that arrive late, look wrong, or go unread.",
    includes: "Preparing readable statements and walking through what they mean for your next decision.",
  },
];

export const GENERAL_FAQS = [
  {
    q: "Who do you work with?",
    a: "Business owners, self-employed professionals, individuals, and families. With over a decade of accounting management experience behind the firm, we’re just as comfortable organizing years of backlogged records as we are setting up a brand-new business correctly from day one.",
  },
  {
    q: "What services do you offer?",
    a: "The full accounting cycle: bookkeeping, accounts payable and receivable, bank reconciliations, payroll support, budgeting, 1099 support, account setup, financial statements and reporting. Plus business and personal tax planning conversations. Every engagement starts by confirming scope together.",
  },
  {
    q: "How do we get started?",
    a: "Book a consultation online, call, or send the inquiry form. We’ll talk through where your records stand, what you need, and whether we’re the right fit. Then confirm the scope before any work begins.",
  },
  {
    q: "Can you take over my books from another bookkeeper?",
    a: "Yes, changing bookkeepers is a common and very workable process. We’ve written a plain-language guide to what a handover involves. See Changing Bookkeepers under Resources.",
  },
  {
    q: "Do you prepare and file tax returns?",
    a: "Our published focus here is planning and readiness: organized records, clear statements, and tax planning conversations. Preparation and filing availability varies, so please ask us directly about what you need and we’ll confirm the current scope.",
  },
  {
    q: "How much do services cost?",
    a: "It depends on the work, the volume, and how your records currently stand. So we don’t publish one-size-fits-all pricing. We’ll confirm exact scope and cost with you before anything begins.",
  },
  {
    q: "Is my information safe on this website?",
    a: "The inquiry form is for general questions only. Please do not include Social Security numbers, account numbers, or tax documents. Once we’re working together, sensitive information is exchanged through secure channels.",
  },
  {
    q: "Do we have to live nearby?",
    a: "Much of our work can be handled remotely. Call us and we’ll confirm how we can work with you in your situation.",
  },
];

export const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: GENERAL_FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export const STEPS = [
  {
    n: "01",
    title: "Say hello",
    body: "Book a consultation, call, or send the inquiry form. Tell us where things stand. No preparation needed for the first conversation.",
  },
  {
    n: "02",
    title: "Get organized",
    body: "We review your records together, set up what’s missing, and bring your books to a place you can trust.",
  },
  {
    n: "03",
    title: "Plan ahead",
    body: "With clean numbers in place, we talk through what’s next. For your business, your household, or both.",
  },
];

export const PATHWAYS = [
  {
    title: "Business Accounting",
    to: "/services/accounting-bookkeeping",
    body: "Bookkeeping, reconciliations, payables and receivables, payroll support, and reports you can actually read. For owners and the self-employed.",
    img: "/img/ill-business.webp",
    alt: "Illustration of a storefront with an open ledger and receipts, in sage green tones",
  },
  {
    title: "Business Tax Planning",
    to: "/tax-planning/business",
    body: "Organized records, preparation readiness, estimated-payment questions, and coordinating business and owner-level decisions.",
    img: "/img/ill-tax-business.webp",
    alt: "Illustration of a compass, calendar, and documents in sage green tones",
  },
  {
    title: "Personal Tax Planning",
    to: "/tax-planning/personal",
    body: "Income changes, self-employment, withholding questions, family changes, and retirement transitions. Planned, not improvised.",
    img: "/img/ill-tax-personal.webp",
    alt: "Illustration of a house, family tree, and paper plane in sage green tones",
  },
];
